/**
 * Image caching composable using IndexedDB
 *
 * Caches images as blobs with TTL for persistent storage across sessions.
 * Automatically cleans up expired entries.
 */

const DB_NAME = 'oneplace-image-cache'
const STORE_NAME = 'images'
const DB_VERSION = 1
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

let dbPromise = null

/**
 * Initialize IndexedDB
 */
function openDB() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' })
        store.createIndex('expiresAt', 'expiresAt')
      }
    }
  })

  return dbPromise
}

/**
 * Get cached image blob URL
 * @param {string} url - The image URL to cache
 * @returns {Promise<string|null>} - Blob URL or null if not cached/expired
 */
export async function getCachedImage(url) {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(url)

      request.onsuccess = () => {
        const entry = request.result
        if (entry && entry.expiresAt > Date.now()) {
          // Return blob URL from cached blob
          resolve(URL.createObjectURL(entry.blob))
        } else {
          resolve(null)
        }
      }

      request.onerror = () => resolve(null)
    })
  } catch {
    return null
  }
}

/**
 * Cache an image blob
 * @param {string} url - The image URL (cache key)
 * @param {Blob} blob - The image blob to cache
 */
export async function setCachedImage(url, blob) {
  try {
    const db = await openDB()
    const entry = {
      url,
      blob,
      expiresAt: Date.now() + CACHE_TTL_MS,
      cachedAt: Date.now(),
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(entry)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } catch (error) {
    console.warn('Failed to cache image:', error)
  }
}

/**
 * Fetch and cache an image
 * @param {string} url - The image URL to fetch and cache
 * @returns {Promise<string|null>} - Blob URL or null on error
 */
export async function fetchAndCacheImage(url) {
  // Try cache first
  const cached = await getCachedImage(url)
  if (cached) return cached

  try {
    // Fetch the image
    const response = await fetch(url)
    if (!response.ok) return null

    const blob = await response.blob()

    // Cache it
    await setCachedImage(url, blob)

    // Return blob URL
    return URL.createObjectURL(blob)
  } catch (error) {
    console.warn('Failed to fetch image:', error)
    return null
  }
}

/**
 * Clean up expired cache entries
 * Call this periodically or on app startup
 */
export async function cleanupExpiredCache() {
  try {
    const db = await openDB()
    const now = Date.now()

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('expiresAt')
      const range = IDBKeyRange.upperBound(now)
      const request = index.openCursor(range)

      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          store.delete(cursor.primaryKey)
          cursor.continue()
        }
      }

      transaction.oncomplete = () => resolve()
      transaction.onerror = () => resolve()
    })
  } catch {
    // Ignore cleanup errors
  }
}

/**
 * Clear entire image cache
 */
export async function clearImageCache() {
  try {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } catch (error) {
    console.warn('Failed to clear image cache:', error)
  }
}

// Run cleanup on module load (non-blocking)
if (typeof window !== 'undefined') {
  cleanupExpiredCache().catch(() => {})
}
